// Personae API contract types — wire shapes for the admin Personae endpoints.
//
// A **persona** is a fictitious company + GC profile that a content-editor
// (typically Olivier) creates to test the AI Coach against controlled
// synthetic profiles. Persona is NOT a tenant — it has no auth, no billing,
// is never signable-into, and has no consumer-pool user. Its sole purpose
// is quality-loop testing of the coaching AI.
//
// The persona attribute schema is a SUPERSET of the tenant + TenantUserProfile
// schemas — reuses the exact same free-string enums (sector, jurisdiction,
// geography) plus adds persona-specific fields (`maturity_per_domain`,
// `business_context_narrative`, `legal_dept_size`).
//
// Design references:
//   - design/UI : UX v1.0/for-engineering-personae/README.md
//   - design/UI : UX v1.0/for-engineering-personae/specs/personae-strings.md
//
// V1 scope:
//   - Two surfaces: `/admin/knowledge/personae` (library) and
//     `/admin/insights/labs` (testbed) — one persona_conversations store, two
//     doors.
//   - RBAC: content_editor + super_admin. Restore is super_admin-only
//     (mirrors Tenants).
//   - Archive is the terminal state; never hard-delete.

import type { Domain } from './domains.js';

// ─── IDs ─────────────────────────────────────────────────────────────────────

/** Stable, opaque persona id — Mongo ObjectId serialized as a 24-char hex string. */
export type PersonaId = string;

/** Stable, opaque persona-conversation ("scenario") id — same format. */
export type PersonaConversationId = string;

// ─── Enums ──────────────────────────────────────────────────────────────────

/**
 * Lifecycle status. Deliberately simpler than tenant/user status:
 *   - `active`   — visible in the default library, usable in Labs
 *   - `archived` — soft-hidden; scenarios kept; restorable by super_admin
 *
 * There is no `removed` state — personae are internal testing tooling, not
 * GDPR subjects, and the extra remove/restore rung would be overkill. Hard
 * delete is never used.
 */
export type PersonaStatus = 'active' | 'archived';

/**
 * Maturity level per legal-ops domain — 5-level CMMI-flavored ladder per
 * Olivier (2026-07-09). Muted chip palette per designer spec.
 */
export type MaturityLevel =
  | 'initial'
  | 'developing'
  | 'established'
  | 'advanced'
  | 'optimized';

export const ALL_MATURITY_LEVELS: ReadonlyArray<MaturityLevel> = [
  'initial',
  'developing',
  'established',
  'advanced',
  'optimized',
] as const;

/**
 * GC career background — closed enum. Matches the designer's dropdown.
 */
export type PersonaBackground =
  | 'in_house'
  | 'private_practice'
  | 'mixed'
  | 'academic'
  | 'other';

export const ALL_PERSONA_BACKGROUNDS: ReadonlyArray<PersonaBackground> = [
  'in_house',
  'private_practice',
  'mixed',
  'academic',
  'other',
] as const;

/**
 * Bucketed years of experience — reuses the same buckets as TenantUserProfile
 * so persona → tenant translation stays 1:1. Optional.
 */
export type PersonaYearsExperience = '0-3' | '4-7' | '8-15' | '15+';

// ─── Persona shape ──────────────────────────────────────────────────────────

/**
 * The maturity-per-domain map. Every one of the 8 domains has a level; the
 * editor forces the user to set all 8 before save (per §4 of the strings
 * spec).
 */
export type PersonaMaturityByDomain = Record<Domain, MaturityLevel>;

/**
 * Company + GC profile attributes for a persona. Superset of tenant +
 * TenantUserProfile. All fields optional at the wire level EXCEPT `name`,
 * `maturity_per_domain`; the editor enforces additional completeness
 * checks before allowing save.
 */
export interface PersonaAttributes {
  // Identity
  name: string;
  sector?: string;
  revenue_band?: string;
  employee_band?: string;
  /** Number of lawyers in the legal team. */
  legal_dept_size?: number;

  // Geography
  /** Where the company operates (ISO country codes, multi-select). */
  geographies?: string[];
  /** Primary legal venue (ISO 3166 alpha-2). */
  primary_jurisdiction?: string;

  // GC profile
  years_experience?: PersonaYearsExperience;
  background?: PersonaBackground;

  // Maturity (all 8 domains required at save time; type stays partial to
  // support in-progress drafts persisted server-side eventually).
  maturity_per_domain: PersonaMaturityByDomain;

  // Business context (free text, injected verbatim into the Coach prompt).
  business_context_narrative?: string;
}

/**
 * Row summary for the personae library table (List page). Denormalized
 * counters keep the list page snappy.
 */
export interface PersonaSummary {
  id: PersonaId;
  name: string;
  sector: string | null;
  revenue_band: string | null;
  primary_jurisdiction: string | null;
  legal_dept_size: number | null;
  status: PersonaStatus;
  /** Number of test scenarios (persona_conversations) currently owned. */
  scenarios_count: number;
  /** ISO-8601. */
  created_at: string;
  /** ISO-8601. */
  updated_at: string;
}

/** Detail view — full attributes + audit metadata + counters. */
export interface Persona extends PersonaSummary {
  attributes: PersonaAttributes;
  /** Display name of the actor who created this persona. */
  created_by: string;
  /** Display name of the actor who last edited. */
  last_edited_by: string;
  /** ISO-8601; set when status flipped to 'archived'. Null otherwise. */
  archived_at: string | null;
  /** Display name of the actor who archived. Null unless status='archived'. */
  archived_by: string | null;
  /** Denormalized aggregate over conversations for the stat card. */
  responses_reviewed_count: number;
}

// ─── Persona-conversation (a "test scenario") ───────────────────────────────

/**
 * A test scenario is a single chat thread scoped to one persona. Olivier can
 * spawn multiple scenarios per persona (e.g. "First 100 days" vs "Panel
 * rationalisation"). Conversations never expire — regression comparison
 * over time is a first-class goal.
 */
export interface PersonaConversationSummary {
  id: PersonaConversationId;
  persona_id: PersonaId;
  /** Free-text, editable in place. Duplicates allowed (keyed by id). */
  name: string;
  created_at: string;
  /** ISO-8601 of the most recent message. Null if the scenario is empty. */
  last_message_at: string | null;
  message_count: number;
  /** Aggregate thumbs-up on assistant messages. */
  up_count: number;
  /** Aggregate thumbs-down on assistant messages. */
  down_count: number;
  /** Aggregate messages where both a thumb AND a note were captured. */
  reviewed_count: number;
}

// ─── Chat messages ──────────────────────────────────────────────────────────

/**
 * A single message inside a scenario. Assistant messages carry snapshot
 * metadata so before/after regression compare works: `kb_snapshot_version`
 * pins the KB revision consulted, `prompt_template_version` pins the
 * prompt-builder revision. Debugging surface ("View prompt & context ▸")
 * hydrates from `debug` when present.
 */
export interface PersonaMessage {
  id: string;
  role: 'user' | 'assistant';
  text: string;
  /** ISO-8601. */
  created_at: string;

  /** Assistant-only. RAG citations to render inline in the message. */
  citations?: ReadonlyArray<PersonaMessageCitation>;

  /** Assistant-only. Feedback captured on this response. */
  feedback?: {
    thumb: 'up' | 'down' | null;
    note: string | null;
    /** ISO-8601 of last feedback change. */
    updated_at: string;
  };

  /** Assistant-only. Snapshot metadata for regression compare. */
  kb_snapshot_version?: string;
  prompt_template_version?: string;

  /**
   * Assistant-only. Debug view payload — what the Coach engine actually saw
   * for this call. Populated on every persona response (not on tenant
   * responses). Enables the "View prompt & context ▸" panel.
   */
  debug?: PersonaMessageDebug;
}

export interface PersonaMessageCitation {
  /** KB entry id the citation points to. */
  entry_id: string;
  entry_type: 'qa_pair' | 'framework' | 'case_study' | 'document_excerpt';
  /** Rendered label (question, framework name, book+chapter, etc.). */
  label: string;
  /** Version-of-KB-entry the citation resolves against (never "current"). */
  version: number;
  /** Similarity score at retrieval time (0-1). */
  score: number;
}

export interface PersonaMessageDebug {
  /** The persona attributes (or any structured context) as injected into
   *  the prompt, verbatim. */
  attributes_injected: Record<string, unknown>;
  /** The system prompt (with any templating already resolved). */
  system_prompt: string;
  /** The user question exactly as passed to the model. */
  user_question: string;
  /**
   * The exact string that was embedded for retrieval — usually equals
   * `user_question`, but Labs may enrich it. Null when this record was
   * produced without RAG (v1 no-RAG runs, still present in Mongo).
   */
  retrieval_query?: string | null;
  /**
   * KB context retrieved by RAG and injected into the prompt. Shape
   * matches the coach engine's `CoachRetrievedContextEntry` (kb_entry_id,
   * type, domain, language, source_ref, title, snippet, similarity_score,
   * was_cited_by_response). Old records (pre-2026-08 RAG rollout) may
   * still have the legacy shape (entry_id, entry_type, version, score,
   * excerpt) — readers should tolerate both until backfill/migration
   * cleans them up. Empty array when no retrieval ran.
   */
  retrieved_context: ReadonlyArray<Record<string, unknown>>;
  /** 1-indexed citation numbers the model produced that ARE in [1..N]. */
  cited_indices?: readonly number[];
  /**
   * Citation numbers the model produced that fall OUTSIDE [1..N] — i.e.
   * the model cited a passage it wasn't given. The Labs bench renders
   * these as danger-styled "fabrication" chips.
   */
  orphan_citation_indices?: readonly number[];
}

// ─── Wire contracts: GET /personae (list) ───────────────────────────────────

export interface ListPersonaeQuery {
  status?: PersonaStatus | 'all';
  /** Free-text search over name + sector. */
  q?: string;
}

export interface ListPersonaeResponse {
  personae: ReadonlyArray<PersonaSummary>;
  counts: {
    total: number;
    active: number;
    archived: number;
    scenarios_run: number;
    responses_reviewed: number;
  };
}

// ─── GET /personae/{id} (detail) ────────────────────────────────────────────

export interface GetPersonaResponse {
  persona: Persona;
  scenarios: ReadonlyArray<PersonaConversationSummary>;
}

// ─── POST /personae (create) ─── PATCH /personae/{id} (edit) ────────────────

export interface CreatePersonaRequest {
  attributes: PersonaAttributes;
}
export interface CreatePersonaResponse {
  persona: Persona;
}

export interface PatchPersonaRequest {
  attributes?: PersonaAttributes;
}
export interface PatchPersonaResponse {
  persona: Persona;
}

// ─── POST /personae/{id}/archive · restore ──────────────────────────────────

export interface ArchivePersonaResponse {
  persona: Persona;
}
export interface RestorePersonaResponse {
  persona: Persona;
}

// ─── Scenarios & chat wire contracts (used by both Labs + Detail) ──────────

export interface ListPersonaScenariosResponse {
  scenarios: ReadonlyArray<PersonaConversationSummary>;
}

export interface CreatePersonaScenarioRequest {
  name?: string;
}
export interface CreatePersonaScenarioResponse {
  scenario: PersonaConversationSummary;
}

export interface RenamePersonaScenarioRequest {
  name: string;
}
export interface RenamePersonaScenarioResponse {
  scenario: PersonaConversationSummary;
}

export interface GetPersonaScenarioMessagesResponse {
  scenario: PersonaConversationSummary;
  messages: ReadonlyArray<PersonaMessage>;
}

export interface PostPersonaScenarioMessageRequest {
  text: string;
}
export interface PostPersonaScenarioMessageResponse {
  user_message: PersonaMessage;
  assistant_message: PersonaMessage;
  scenario: PersonaConversationSummary;
}

// ─── Feedback (thumbs + note) on a specific assistant message ──────────────

export interface PostPersonaMessageFeedbackRequest {
  thumb: 'up' | 'down' | null;
  note?: string | null;
}
export interface PostPersonaMessageFeedbackResponse {
  message: PersonaMessage;
  scenario: PersonaConversationSummary;
}
